<template>
<div>
  <!--    Breadcrumbs-->
  <breadcrumb style="margin-left: -15px">
    <breadcrumb-item>
      <a href="/settings/company">Company settings</a>
    </breadcrumb-item>
    <breadcrumb-item>
      <span>{{ isCreateMode ? 'New location' : group.name }}</span>
    </breadcrumb-item>
  </breadcrumb>
  <!--    Loader-->
  <loading-panel :loading="loading"></loading-panel>

  <div class="row d-flex justify-content-center">
    <div class="col-lg-4">
      <card class="card-user card-user--compact">
        <div class="author">
          <div class="block block-one"></div>
          <div class="block block-two"></div>
          <div class="block block-three"></div>
          <div class="block block-four"></div>
          <a href="javascript:void(0)">
            <img v-if="group.img != null" class="avatar" :src="group.img" alt="..." />
            <img v-if="group.img == null" class="avatar" src="https://console.offthecouch.io/img/placeholder.jpg" alt="..." />
          </a>
          <div>
            <span class="btn btn-primary btn-sm btn-simple btn-file">Change group image
                <input
                  accept="image/*"
                  @change="handleImageUpload"
                  type="file"
                  name="..."
                  class="valid"
                  :multiple="false"
                  aria-invalid="false"
                />
            </span>
          </div>
        </div>
        <h3 slot="header" class="card-title">{{ isCreateMode ? 'New location' : group.name }}</h3>
        <div class="card-description">
          <div class="row">
            <div class="col">
              <base-input required label="Group name"
                          type="text"
                          placeholder="Group name"
                          v-model="group.name"
                          :error="errs.group_name"
              >
              </base-input>
            </div>
          </div>
          <div class="row">
            <div class="col">
              <label>
                <div>Group code
                  <el-popover trigger="hover"
                              placement="right">
                    <div>
                      <div class="popover-body">A short, unique identifier for this group.<br>
                        This code is used in customer-facing links such as waivers and booking sites.<br>
                        It cannot be the same as any other group code in the system.</div>
                    </div>
                    <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                  </el-popover>
                </div>
              </label>
              <base-input required
                          type="text"
                          placeholder="Group code"
                          v-model="group.code"
                          :error="errs.group_code"
              >
              </base-input>
            </div>
            <div class="col">
              <base-input label="Company number"
                          type="text"
                          placeholder="Company number"
                          v-model="group.company_number"
              >
              </base-input>
            </div>
          </div>
          <div class="row">
            <div class="col">
              <base-input label="Description">
                <el-input
                  v-model="group.description"
                  :rows="3"
                  type="textarea"
                  placeholder="Group description"
                />
              </base-input>
            </div>
          </div>
        </div>

        <div class="row mt-2" :class="{ 'mb-4': isCreateMode }">
          <div class="col">
            <base-button v-on:click="isCreateMode ? createGroup() : updateGroup()" type="primary">
              {{ isCreateMode ? 'Create group' : 'Update group' }}
            </base-button>
          </div>
        </div>

      </card>
    </div>

<!--    Locale, Games-->
    <div class="col-lg-8">
      <card>
<!--        Sections-->
        <div class="row mb-3">
          <div class="col">
            <tab-rail
              :tabs="categories"
              :value="activeTabIndex"
              @input="switchTab"
            />
          </div>
        </div>

<!--        Location details-->
        <div v-if="activeTabIndex === 0">
          <collapsible-section title="Contact" icon="tim-icons icon-email-85" :collapsible="false">
          <div class="row">
            <div class="col-md-7">
              <base-input label="Email"
                          type="text"
                          placeholder="Email"
                          v-model="group.email"
              >
              </base-input>
            </div>
            <div class="col-md-5">
              <base-input
                type="text"
                label="Phone">
                <phone-number-input ref="phoneInput"
                                    v-model="group.phone" @phone-update="handlePhoneUpdate"
                                    :default-country="countryCode || 'US'"
                />
              </base-input>
            </div>
          </div>
          </collapsible-section>
          <collapsible-section title="Address"
                               icon="tim-icons icon-pin"
                               :collapsible="false"
                               class="mt-4">
          <div class="row align-items-stretch">
          <div :class="isCreateMode ? 'col-12' : 'col-lg-6'">
          <div class="row">
            <div class="col">
              <address-search-input v-model="group.address_line_1"
                                    :country-code="group.country && group.country.code ? group.country.code : ''"
                                    @pick="onAddressPick" />
            </div>
          </div>
          <div class="row">
            <div class="col">
              <base-input label="Address line 2"
                          type="text"
                          placeholder="Address line 2"
                          v-model="group.address_line_2"
              >
              </base-input>
            </div>
          </div>

          <div class="row">
            <div class="col-sm-6">
              <base-input :label="group.country.city_label"
                          type="text"
                          :placeholder="group.country.city_label"
                          v-model="group.city"
              >
              </base-input>
            </div>
            <div class="col-sm-6">
              <base-input v-if="group.country.state_label === 'State'" :label="group.country.state_label">
                <el-select
                  v-model="group.state"
                  class="select-primary"
                  name="email_group"
                  :placeholder="group.country.state_label"
                >
                  <el-option
                    v-for="(s, i) in states"
                    class="select-primary"
                    :label="s.name"
                    :value="s.name"
                    :key="'S' + i"
                  ></el-option>
                </el-select>
              </base-input>
              <base-input v-else :placeholder="group.country.state_label" :label="group.country.state_label" v-model="group.state"></base-input>
            </div>
            <div class="col-sm-6">
              <base-input :label="group.country.zip_label"
                          type="text"
                          :placeholder="group.country.zip_placeholder"
                          v-model="group.zip"
              >
              </base-input>
            </div>
            <div class="col-sm-6">
              <base-input label="Country">
                <el-select
                           v-model="group.country"
                           class="select-primary"
                           name="email_group"
                           placeholder="Country"
                           value-key="code"
                >
                  <el-option
                    v-for="(c, i) in countries"
                    class="select-primary"
                    :label="c.name"
                    :value="{name: c.name, code: c.code, city_label: c.city_label,
                      state_label: c.state_label, zip_label: c.zip_label, zip_placeholder: c.zip_placeholder,
                      phone_placeholder: c.phone_placeholder}"
                    :key="'C' + i"
                  ></el-option>
                </el-select>
              </base-input>
            </div>
          </div>
          </div>
          <div v-if="!isCreateMode" class="col-lg-6 d-flex flex-column">
            <venue-pin-field ref="venuePin"
                             :company-id="companyIdForPin"
                             :group-id="$route.query.id"
                             :can-edit="true"
                             :has-address="groupHasAddress" />
          </div>
          </div>
          </collapsible-section>
          <div v-if="!isCreateMode" class="row mt-4">
            <div class="col">
              <base-button v-on:click="updateGroup()" type="primary">
                Update location details
              </base-button>
            </div>
          </div>
        </div>
        <!--        Locale-->
        <div v-if="activeTabIndex === 1">
          <collapsible-section title="Locale" icon="tim-icons icon-world" :collapsible="false">
            <div class="row">
              <div class="col-lg-4 col-md-6">
                <base-input label="Currency">
                  <el-select
                    v-model="group.currency"
                    class="select-primary"
                    name="currency"
                    placeholder="Currency"
                    v-on:change="changeCurrencyFormats()"
                  >
                    <el-option
                      v-for="(c, i) in currencies"
                      class="select-primary"
                      :label="c.currency + ' (' + c.symbol + ')'"
                      :value="c.code"
                      :key="'C' + i"
                    ></el-option>
                  </el-select>
                </base-input>
              </div>
              <div class="col-lg-4 col-md-6">
                <base-input label="Timezone">
                  <el-select style="width: 100%"
                             class="select-primary"
                             v-model="group.tz_name"
                             filterable
                             placeholder="Select timezone"
                             :filter-method="filterTimezones"
                             @visible-change="onTimezoneDropdownToggle"
                             @change="handleTimezoneChange"
                  >
                    <!-- Groups are keyed on the query: el-select recomputes an option
                         group's v-show from its children synchronously inside
                         filter-method, before Vue re-renders, so a group that becomes
                         non-empty would otherwise stay hidden. Re-keying remounts it. -->
                    <el-option-group v-if="countryTimezones.length"
                                     :key="'tz-country-' + tzQuery"
                                     :label="countryTimezoneLabel">
                      <el-option v-for="tz in countryTimezones"
                                 :key="tz.value"
                                 :value="tz.value"
                                 :label="tz.name"
                      >
                        <span>{{ tz.value }} - {{ tz.abbr }} ({{ tz.offset }})</span>
                      </el-option>
                    </el-option-group>
                    <el-option-group v-if="visibleOtherTimezones.length"
                                     :key="'tz-other-' + tzQuery"
                                     :label="otherTimezoneLabel">
                      <el-option v-for="tz in visibleOtherTimezones"
                                 :key="tz.value"
                                 :value="tz.value"
                                 :label="tz.name"
                      >
                        <span>{{ tz.value }} - {{ tz.abbr }} ({{ tz.offset }})</span>
                      </el-option>
                    </el-option-group>
                    <div v-if="hasMoreTimezones"
                         v-infinite-scroll="loadMoreTimezones"
                         infinite-scroll-immediate="false"
                         infinite-scroll-distance="20"
                         class="text-center text-muted py-1"
                    >
                      Loading more...
                    </div>
                  </el-select>
                </base-input>
              </div>
              <div class="col-lg-4 col-md-6">
                <label>
                  <div>Default language
                    <el-popover trigger="hover"
                                placement="right">
                      <div>
                        <div class="popover-body">Customer emails and booking pages default to this language.</div>
                      </div>
                      <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                    </el-popover>
                  </div>
                </label>
                <base-input :error="errs.default_locale">
                  <el-select
                    v-model="group.default_locale"
                    class="select-primary"
                    name="default_locale"
                    placeholder="Default language"
                  >
                    <el-option
                      v-for="(l, i) in supported_languages"
                      class="select-primary"
                      :label="l.label"
                      :value="l.code"
                      :key="'L' + i"
                    ></el-option>
                  </el-select>
                </base-input>
              </div>
            </div>
          </collapsible-section>

          <collapsible-section title="Formats" icon="tim-icons icon-calendar-60" :collapsible="false" class="mt-4">
            <div class="row">
              <div class="col-lg-4">
                <base-input label="Date format">
                  <el-select
                    v-model="group.date_format"
                    class="select-primary"
                    name="date_format"
                    placeholder="Date format"
                    value-key="format"
                  >
                    <el-option
                      v-for="(d, i) in date_formats"
                      class="select-primary"
                      :label="d.example + ' (' + d.format + ')'"
                      :value="{format: d.format, example: d.example}"
                      :key="'D' + i"
                    ></el-option>
                  </el-select>
                </base-input>
              </div>
              <div class="col-lg-4">
                <base-input label="Time format">
                  <el-select
                    v-model="group.time_format"
                    class="select-primary"
                    name="time_format"
                    placeholder="Time format"
                    value-key="format"
                  >
                    <el-option
                      v-for="(f, i) in time_formats"
                      class="select-primary"
                      :label="f.example + ' (' + f.format + ')'"
                      :value="{format: f.format, example: f.example}"
                      :key="'F' + i"
                    ></el-option>
                  </el-select>
                </base-input>
              </div>
              <div class="col-lg-4">
                <base-input label="First day of the week">
                  <el-select
                    v-model="group.first_day_of_week"
                    class="select-primary"
                    name="first_day_of_week"
                    placeholder="First day of the week"
                    value-key="id"
                  >
                    <el-option
                      v-for="(w, i) in days_of_the_week"
                      class="select-primary"
                      :label="w.name"
                      :value="{id: w.id, name: w.name}"
                      :key="'W' + i"
                    ></el-option>
                  </el-select>
                </base-input>
              </div>
            </div>
            <div class="row mt-3">
              <div class="col-lg-4">
                <label>
                  <div>New day starts at
                    <el-popover trigger="hover"
                                placement="right"
                                width="320">
                      <div>
                        <div class="popover-body" style="text-align: left; word-break: normal; overflow-wrap: break-word">Set this if your business day runs past midnight. Until this time, pages showing the current day keep showing the previous day, so late groups can still check in after midnight. For example, a venue running games until 1:30 AM might set this to 2:00 AM.</div>
                      </div>
                      <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                    </el-popover>
                  </div>
                </label>
                <base-input>
                  <el-time-picker
                    style="width: 100%"
                    v-model="group.day_rollover_time"
                    :format="dayRolloverPickerFormat"
                    :picker-options="{ selectableRange: '00:00:00 - 08:00:00' }"
                    placeholder="Select time"
                  />
                </base-input>
              </div>
              <div class="col-lg-4">
                <div class="togglebutton switch-change-color mt-4">
                  <el-switch active-color="#e14eca"
                             v-model="group.show_day_of_week" />
                  <label> &nbsp; Display day of the week</label>
                </div>
              </div>
            </div>
            <div class="row mt-3">
              <div class="col-lg-6">
                <base-input label="Currency format">
                  <el-select
                    v-model="group.currency_format"
                    class="select-primary"
                    name="currency_format"
                    placeholder="Currency format"
                    value-key="id"
                  >
                    <el-option
                      v-for="(cf, i) in currency_formats"
                      class="select-primary"
                      :label="cf.example"
                      :value="{id: cf.id}"
                      :key="'CF' + i"
                    ></el-option>
                  </el-select>
                </base-input>
              </div>
              <div class="col-lg-6">
                <base-input label="Number format">
                  <el-select
                    v-model="group.number_format"
                    class="select-primary"
                    name="number_format"
                    placeholder="Number format"
                    value-key="id"
                  >
                    <el-option
                      v-for="(n, i) in number_formats"
                      class="select-primary"
                      :label="n.example"
                      :value="{ id: n.id }"
                      :key="'N' + i"
                    ></el-option>
                  </el-select>
                </base-input>
              </div>
            </div>
          </collapsible-section>

          <div v-if="!isCreateMode" class="row mt-4">
            <div class="col">
              <base-button v-on:click="updateGroup()" type="primary">
                Update locale and formatting
              </base-button>
            </div>
          </div>
        </div>

        <div v-if="activeTabIndex === 3 && !isCreateMode">
          <div v-if="!selectedIntegration">
            <h4 class="card-title">Marketing</h4>
            <div class="row">
              <div class="col">
                <el-switch active-color="#e14eca" v-on:input="savePurchaseTracking"
                           v-model="group.enable_utm_source_tracking"  />
                <label> &nbsp; Enable purchase source tracking
                  <el-popover trigger="hover" placement="right">
                    <div>
                      <div class="popover-body">Will display where a booking, gift card, or item purchase came from.
                      </div>
                    </div>
                    <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                  </el-popover>
                </label>
              </div>
            </div>

            <div class="row" v-if="group.enable_utm_source_tracking">
              <div class="col">
                <base-button link type="primary" style="padding-left: 0px" @click="openLinkBuilder">Generate tracking link</base-button>
              </div>
            </div>
            <div class="row mt-3">
              <!--          Google tag-->

              <div class="col-xl-4 col-lg-6 col-md-6" v-for="(p, i) in integrations" :key="'I' + i">
                <card class="card-white">
                  <div class="row">
                    <div class="col text-right">
                      <span v-if="p.id === 1">
                         <badge v-if="group.google_tag_active" type="success">active</badge>
                        <badge v-else type="default">inactive</badge>
                      </span>
                      <span v-if="p.id === 2">
                         <badge v-if="group.pixel_active" type="success">active</badge>
                        <badge v-else type="default">inactive</badge>
                      </span>
                      <span v-if="p.id === 3">
                         <badge v-if="group.ga4_active" type="success">active</badge>
                        <badge v-else type="default">inactive</badge>
                      </span>
                    </div>
                  </div>
                  <div class="row justify-content-center">
                    <img :src="p.logo" style="object-fit: scale-down" height="60"/>
                  </div>
                  <div class="row mt-1 justify-content-center">
                    {{p.name}}
                  </div>
                  <div class="row mt-2">
                    <div class="col">
                      <base-button block v-on:click="configureIntegration(p)" type="info">Configure integration</base-button>
                    </div>
                  </div>
                </card>
              </div>
            </div>
            <h4 class="card-title">Productivity</h4>
            <div class="row">
              <!--          Google tag-->

              <div class="col-xl-4 col-lg-6 col-md-6" v-for="(pr, i) in productivity_integrations" :key="'PR' + i">
                <card class="card-white">
                  <div class="row">
                    <div class="col text-right">
                      <span v-if="pr.id === 4">
                         <badge v-if="group.gcal_active" type="success">active</badge>
                        <badge v-else type="default">inactive</badge>
                      </span>
                    </div>
                  </div>
                  <div class="row justify-content-center">
                    <img :src="pr.logo" style="object-fit: scale-down" height="60"/>
                  </div>
                  <div class="row mt-1 justify-content-center">
                    {{pr.name}}
                  </div>
                  <div class="row mt-2">
                    <div class="col">
                      <base-button block v-on:click="configureIntegration(pr)" type="info">Configure integration</base-button>
                    </div>
                  </div>
                </card>
              </div>
            </div>
          </div>

<!--          Google tag-->
          <div v-if="selectedIntegration.id === 1">
            <div class="row">
              <div class="col">
                <h4 class="card-title">Google Tag Manager
                  <img :src="selectedIntegration.logo" style="object-fit: scale-down" height="20"/>
                </h4>
              </div>
            </div>

            <div class="row">
              <div class="col">
                <p>We've created a pre-configured GTM container template to help you track conversions on your booking platform.</p>

                <div class="row mb-2">
                  <div class="col">
                    <div class="d-flex align-items-center mt-2 p-2 border border-primary rounded">
                      <i class="text-white fas fa-info-circle mr-2"></i>
                      <small class="text-white"><strong>What's included:</strong> Purchase, add to cart, begin checkout, and success events with automatic currency and value tracking.</small>
                    </div>
                  </div>
                </div>

                <p class="card-title mt-3">Setup steps:</p>
                <ol>
                  <li><strong>Download the template:</strong> Click the button below to download the GTM configuration file</li>
                  <li><strong>Import to GTM:</strong> Go to your <a href="https://tagmanager.google.com/" target="_blank">Google Tag Manager</a> → Admin → Import Container</li>
                  <li><strong>Select the file:</strong> Choose the downloaded file and select "Merge" as the import option</li>
                  <li><strong>Update Measurement ID:</strong> In GTM Variables, find "GA4 Measurement ID" and replace <code>G-XXXXXXXXXX</code> with your actual GA4 ID</li>
                  <li><strong>Publish:</strong> Click Submit → Publish in GTM</li>
                  <li><strong>Test:</strong> Use GTM Preview mode to verify events are firing correctly</li>
                </ol>

                <div class="row mt-3">
                  <div class="col-lg-6">
                    <base-button type="primary" @click="downloadGTMTemplate">
                      <i class="tim-icons icon-cloud-download-93 mr-1"></i>
                      Download GTM template
                    </base-button>
                  </div>
                </div>
              </div>
            </div>

            <hr style="border-top: 1px solid #f4f5f7;" class="mt-4">

            <div class="row">
              <div class="col-lg-6">
                <base-input v-model="group.google_tag_container_id" label="Container ID"></base-input>
              </div>
            </div>
            <div class="row">
              <div class="col-lg-6">
                <div class="togglebutton switch-change-color mt-3">
                  <el-switch active-color="#e14eca"
                             v-model="group.google_tag_active"  />
                  <label> &nbsp; Active</label>
                </div>
              </div>
            </div>
          </div>

          <!--         Meta Pixel-->
          <div v-if="selectedIntegration.id === 2">
            <div class="row">
              <div class="col">
                <h4 class="card-title">Meta Pixel
                  <img :src="selectedIntegration.logo" style="object-fit: scale-down" height="30"/>
                </h4>
              </div>
            </div>
            <div class="row">
              <div class="col-lg-6">
                <base-input v-model="group.pixel_id" label="Meta Pixel ID"></base-input>
              </div>
            </div>
            <div class="row">
              <div class="col-lg-6">
                <div class="togglebutton switch-change-color mt-3">
                  <el-switch active-color="#e14eca"
                             v-model="group.pixel_active"  />
                  <label> &nbsp; Active</label>
                </div>
              </div>
            </div>
            <p class="mt-3">Important: when the booking page is embedded in an iframe, your site has to fire the analytics and pixel events.
              The booking page sends each event to the parent page with postMessage.
              Add the snippet below to your page once; it forwards events to Google Analytics and to the Meta pixel when those are installed on your page.
              Without the snippet, the Meta pixel fires inside the booking frame, where Meta cannot attribute purchases to your ads or report their value.</p>
            <div class="mt-2">
              <base-alert type="white" class="text-muted">

                <div class="row">
                  <div class="col-10" style="padding-right: 0">
                    <code>{{ embedded_text }}</code>
                  </div>
                  <div class="col text-right">
                    <base-button class="like btn-link"
                                 type="default"
                                 size="sm" style="padding: 0px 0px 2px"

                                 v-on:click="copy(embedded_text)">
                      <i class="tim-icons icon-single-copy-04"></i>
                    </base-button>
                  </div>
                </div>
              </base-alert>
            </div>
            <div class="row">
              <div class="col">
                <el-switch active-color="#e14eca"
                           v-model="group.send_full_cart_amount_to_pixel" />
                <label> &nbsp; Send full cart amount to Meta
                  <el-popover trigger="hover" placement="right">
                    <div>
                      <div class="popover-body">When enabled, the cart total amount is sent to the Meta pixel during customer checkout. <br/>
                        Otherwise, it sends the due amount as determined by deposit or pay on arrival configurations.
                      </div>
                    </div>
                    <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                  </el-popover>
                </label>
              </div>
            </div>
          </div>

          <!--         GA4-->
          <div v-if="selectedIntegration.id === 3">
            <div class="row">
              <div class="col">
                <h4 class="card-title">Google Analytics 4
                  <img :src="selectedIntegration.logo" style="object-fit: scale-down" height="30"/>
                </h4>
              </div>
            </div>
            <div class="row">
              <div class="col-lg-6">
                <base-input v-model="group.ga4_id" label="Measurement ID"></base-input>
              </div>
            </div>
            <div class="row">
              <div class="col-lg-6">
                <div class="togglebutton switch-change-color mt-3">
                  <el-switch active-color="#e14eca"
                             v-model="group.ga4_active"  />
                  <label> &nbsp; Active</label>
                </div>
              </div>
            </div>

            <p class="mt-3">Important: when the booking page is embedded in an iframe, your site has to fire the analytics and pixel events.
              The booking page sends each event to the parent page with postMessage.
              Add the snippet below to your page once; it forwards events to Google Analytics and to the Meta pixel when those are installed on your page.
              Without the snippet, the Meta pixel fires inside the booking frame, where Meta cannot attribute purchases to your ads or report their value.</p>
            <div class="mt-2">
              <base-alert type="white" class="text-muted">

                <div class="row">
                  <div class="col-10" style="padding-right: 0">
                    <code>{{ embedded_text }}</code>
                  </div>
                  <div class="col text-right">
                    <base-button class="like btn-link"
                                 type="default"
                                 size="sm" style="padding: 0px 0px 2px"

                                 v-on:click="copy(embedded_text)">
                      <i class="tim-icons icon-single-copy-04"></i>
                    </base-button>
                  </div>
                </div>
              </base-alert>
            </div>
            <div class="row">
              <div class="col">
                <el-switch active-color="#e14eca"
                           v-model="group.send_full_cart_amount_to_ga" />
                <label> &nbsp; Send full cart amount to GA
                  <el-popover trigger="hover" placement="right">
                    <div>
                      <div class="popover-body">When enabled, the cart total amount will be sent to GA during customer checkout. <br/>
                        Otherwise, it will send the due amount as determined by deposit or pay on arrival configurations.
                      </div>
                    </div>
                    <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                  </el-popover>
                </label>
              </div>
            </div>
          </div>

          <!--          GCal-->
          <div v-if="selectedIntegration.id === 4">
            <div class="row">
              <div class="col">
                <h4 class="card-title">Google calendar
                  <img :src="selectedIntegration.logo" style="object-fit: scale-down" height="20"/>
                </h4>
              </div>
            </div>
            <div class="row">
              <div class="col">
                <p>To set up the integration with Google Calendar,
                  you must first create a Service Account in Google Cloud and upload the credentials file to Off The Couch.
                  This allows our app to securely communicate with Google Calendar on your behalf. Follow these steps: </p>
                <p class="mt-3 card-title">1. Create a Service Account</p>
                <ul>
                  <li>Go to the <a target="_blank" href="https://console.cloud.google.com/">Google Cloud Console</a>.</li>
                  <li>Navigate to <b>IAM & Admin</b> > <b>Service Accounts</b> and click <b>Create Service Account</b>.</li>
                  <li>Name the service account and assign it the <b>Owner</b> role.</li>
                </ul>
                <p class="mt-3 card-title">2. Enable the Calendar API</p>
                <ul>
                  <li>Go to <b>APIs & Services</b> > <b>Library</b> in <b>Google Cloud Console</b>.</li>
                  <li>Search for "Google Calendar API" and click <b>Enable</b>.</li>
                </ul>
                <p class="mt-3 card-title">3. Generate a key file</p>
                <ul>
                  <li>In the <b>Service Accounts</b> page, select the service account you created.</li>
                  <li>Click <b>Keys</b> > <b>Add Key</b> > <b>Create New Key</b>.</li>
                  <li>Choose JSON format and download the key file.</li>
                </ul>
                <p class="mt-3 card-title">4. Add your service account to calendar</p>
                <ul>
                  <li>For your desired Calendar, navigate to <b>Calendar Settings</b> and scroll to <b>Share with specific people or groups</b>.</li>
                  <li>Click <b>Add people and groups</b> and add the email of your service account with <b>Make changes to events</b> permission selected.</li>
                </ul>
                <p class="mt-3 card-title">5. Upload credentials to Off The Couch</p>
                <ul>
                  <li>Locate the service account email and the private key in the downloaded JSON file and paste them into the form below.</li>
                  <li>For your desired Calendar, navigate to <b>Calendar Settings</b> and scroll to <b>Integrate Calendar</b> section to get your Calendar ID.</li>
                  <li>Off The Couch will now be able to create and modify Google Calendar events.</li>
                </ul>
                <p class="mt-3 card-title">6. Configure custom event fields</p>
                <ul>
                  <li>Use dynamic variables to configure <code>Title</code> and <code>Descriptions</code> fields for your events.</li>
                </ul>
                <p class="mt-3 card-title text-primary">Supported dynamic variables
                  <i class="tim-icons icon-minimal-down ml-1" v-on:click="show_dynamic_variables = !show_dynamic_variables" style="cursor: pointer"></i>
                </p>
                <div v-if="show_dynamic_variables">
                  <div>
                    <code>[Booking Id]
                      <el-tooltip content="Copy"
                                  effect="light"
                                  :open-delay="300"
                                  placement="top">
                        <base-button
                          @click.native="copy('[Booking Id]')"
                          class="like btn-link text-white"
                          size="sm"
                          icon
                        ><i class="tim-icons icon-single-copy-04"></i>
                        </base-button>
                      </el-tooltip>
                    </code>
                  </div>
                  <div>
                    <code>[Game Name]
                      <el-tooltip content="Copy"
                                  effect="light"
                                  :open-delay="300"
                                  placement="top">
                        <base-button
                          @click.native="copy('[Game Name]')"
                          class="like btn-link text-white"
                          size="sm"
                          icon
                        ><i class="tim-icons icon-single-copy-04"></i>
                        </base-button>
                      </el-tooltip>
                    </code>
                  </div>
                  <div>
                    <code>[Group Size]
                      <el-tooltip content="Copy"
                                  effect="light"
                                  :open-delay="300"
                                  placement="top">
                        <base-button
                          @click.native="copy('[Group Size]')"
                          class="like btn-link text-white"
                          size="sm"
                          icon
                        ><i class="tim-icons icon-single-copy-04"></i>
                        </base-button>
                      </el-tooltip>
                    </code>
                  </div>
                  <div>
                    <code>[Customer First Name]
                      <el-tooltip content="Copy"
                                  effect="light"
                                  :open-delay="300"
                                  placement="top">
                        <base-button
                          @click.native="copy('[Customer First Name]')"
                          class="like btn-link text-white"
                          size="sm"
                          icon
                        ><i class="tim-icons icon-single-copy-04"></i>
                        </base-button>
                      </el-tooltip>
                    </code>
                  </div>
                  <div>
                    <code>[Customer Last Name]
                      <el-tooltip content="Copy"
                                  effect="light"
                                  :open-delay="300"
                                  placement="top">
                        <base-button
                          @click.native="copy('[Customer Last Name]')"
                          class="like btn-link text-white"
                          size="sm"
                          icon
                        ><i class="tim-icons icon-single-copy-04"></i>
                        </base-button>
                      </el-tooltip>
                    </code>
                  </div>
                  <div>
                    <code>[Customer Phone]
                      <el-tooltip content="Copy"
                                  effect="light"
                                  :open-delay="300"
                                  placement="top">
                        <base-button
                          @click.native="copy('[Customer Phone]')"
                          class="like btn-link text-white"
                          size="sm"
                          icon
                        ><i class="tim-icons icon-single-copy-04"></i>
                        </base-button>
                      </el-tooltip>
                    </code>
                  </div>
                  <div>
                    <code>[Customer Email]
                      <el-tooltip content="Copy"
                                  effect="light"
                                  :open-delay="300"
                                  placement="top">
                        <base-button
                          @click.native="copy('[Customer Email]')"
                          class="like btn-link text-white"
                          size="sm"
                          icon
                        ><i class="tim-icons icon-single-copy-04"></i>
                        </base-button>
                      </el-tooltip>
                    </code>
                  </div>
                  <div>
                    <code>[Transaction Id]
                      <el-tooltip content="Copy"
                                  effect="light"
                                  :open-delay="300"
                                  placement="top">
                        <base-button
                          @click.native="copy('[Transaction Id]')"
                          class="like btn-link text-white"
                          size="sm"
                          icon
                        ><i class="tim-icons icon-single-copy-04"></i>
                        </base-button>
                      </el-tooltip>
                    </code>
                  </div>
                  <div>
                    <code>[Transaction Link]
                      <el-tooltip content="Copy"
                                  effect="light"
                                  :open-delay="300"
                                  placement="top">
                        <base-button
                          @click.native="copy('[Transaction Link]')"
                          class="like btn-link text-white"
                          size="sm"
                          icon
                        ><i class="tim-icons icon-single-copy-04"></i>
                        </base-button>
                      </el-tooltip>
                    </code>
                  </div>
                  <div>
                    <code>[Amount Paid]
                      <el-tooltip content="Copy"
                                  effect="light"
                                  :open-delay="300"
                                  placement="top">
                        <base-button
                          @click.native="copy('[Amount Paid]')"
                          class="like btn-link text-white"
                          size="sm"
                          icon
                        ><i class="tim-icons icon-single-copy-04"></i>
                        </base-button>
                      </el-tooltip>
                    </code>
                  </div>
                  <div>
                    <code>[Amount Due]
                      <el-tooltip content="Copy"
                                  effect="light"
                                  :open-delay="300"
                                  placement="top">
                        <base-button
                          @click.native="copy('[Amount Due]')"
                          class="like btn-link text-white"
                          size="sm"
                          icon
                        ><i class="tim-icons icon-single-copy-04"></i>
                        </base-button>
                      </el-tooltip>
                    </code>
                  </div>
                </div>
              </div>
            </div>
            <hr style="border-top: 1px solid #f4f5f7;">
            <div v-if="edit_gcal">
              <div class="row">
                <div class="col">
                  <base-input :error="errs.gcal_client_email" v-model="group.gcal_client_email"
                              placeholder="your-service-account@your-project-id.iam.gserviceaccount.com"
                              label="Service account email (client email)"></base-input>
                </div>
                <div class="col-lg-6">
                  <base-input :error="errs.calendar_id"
                              placeholder="team-calendar-id@group.calendar.google.com"
                              v-model="group.calendar_id" label="Calendar ID"></base-input>
                </div>

              </div>
              <div class="row">
                <div class="col">
                  <base-input label="Private key" :error="errs.gcal_private_key">
                    <el-input
                      v-model="gcal_private_key"
                      :rows="7"
                      type="textarea"
                      placeholder="Your service account private key goes here..."
                    />
                  </base-input>
                </div>
              </div>
            </div>
            <div v-else>
              <div class="row">
                <div class="col">
                  <label>Service account email (client email)
                  </label>
                  <p>{{group.gcal_client_email}}</p>
                </div>
                <div class="col-lg-3 text-right">
                  <base-button link size="sm" type="primary" v-on:click="edit_gcal = true">
                    <i class="tim-icons icon-refresh-02 mr-1"></i> Change credentials
                  </base-button>
                </div>
              </div>
              <div class="row">
                <div class="col-lg-6">
                  <label>Calendar ID</label>
                  <p>{{group.calendar_id}}</p>
                </div>
              </div>
            </div>

            <div class="row">
              <div class="col">
                <base-input :error="errs.gcal_event_title"
                            placeholder="Event summary"
                            v-model="group.gcal_event_title" label="Calendar event summary"></base-input>
              </div>
            </div>
            <div class="row">
              <div class="col">
                <base-input label="Calendar event description">
                  <el-input
                    v-model="group.gcal_event_description"
                    :rows="3"
                    type="textarea"
                    placeholder="Add a custom description..."
                  />
                </base-input>
              </div>
            </div>

            <div class="row">
              <div class="col-lg-6">
                <div class="togglebutton switch-change-color mt-3">
                  <el-switch active-color="#e14eca"
                             v-model="group.gcal_active"  />
                  <label> &nbsp; Active</label>
                </div>
              </div>
            </div>
          </div>

          <div v-if="selectedIntegration">
            <div class="row mt-2">
              <div class="col">
                <base-button v-on:click="cancelIntegration()" type="default">
                  Cancel
                </base-button>
                <base-button class="ml-2" v-on:click="updateIntegration()" type="primary">
                  Save
                </base-button>
              </div>
            </div>
          </div>

        </div>

<!--        Resources-->
        <div v-if="activeTabIndex === 2">
          <collapsible-section
            title="Automation"
            icon="tim-icons icon-settings-gear-63"
            description="How resource coverage and buffers decide which slots get blocked, assigned, and booked."
          >
          <div class="row">
            <div class="col">
              <el-switch active-color="#e14eca"
                         v-model="group.require_full_buffer_coverage"  />
              <label> &nbsp; Require full buffer coverage
                <el-popover trigger="hover" placement="right" width="260" popper-class="otc-pop">
                  <div>
                    <div class="popover-body">When on, a resource must cover an event's full setup and cleanup window. When off, buffers may extend past the edges of a shift.
                    </div>
                  </div>
                  <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                </el-popover>
              </label>
            </div>
          </div>
          <div class="row">
            <div class="col">
              <el-switch active-color="#e14eca"
                         v-model="group.cross_game_buffers"  />
              <label> &nbsp; Buffers apply across events
                <el-popover trigger="hover" placement="right" width="260" popper-class="otc-pop">
                  <div>
                    <div class="popover-body">When on, a booking's buffer time counts against resource capacity for all events. When off, it counts only against the same event, so other events can use the resource during setup and cleanup windows.
                    </div>
                  </div>
                  <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                </el-popover>
              </label>
            </div>
          </div>
          <div class="row">
            <div class="col">
              <el-switch active-color="#e14eca"
                         v-model="group.reoptimize_resource_assignments"  />
              <label> &nbsp; Re-optimize resource assignments
                <el-popover trigger="hover" placement="right" width="260" popper-class="otc-pop">
                  <div>
                    <div class="popover-body">When on, every booking, cancellation, reschedule, or resource change re-solves that day's automatic assignments so resources run back to back with as few idle gaps as possible. Assignments made or locked by staff never move.
                    </div>
                  </div>
                  <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                </el-popover>
              </label>
            </div>
          </div>
          <div class="row mt-2" v-if="group.reoptimize_resource_assignments">
            <div class="col-md-4 col-6">
              <label class="text-white">Do not move bookings starting within (hours)
                <el-popover trigger="hover" placement="right" width="260" popper-class="otc-pop">
                  <div>
                    <div class="popover-body">Bookings that already started, or start within this many hours, keep their current resources. Use 0 to allow moves right up to the start time. Maximum 24.
                    </div>
                  </div>
                  <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                </el-popover>
              </label>
              <el-input-number style="width: 100%" v-model="group.reoptimize_freeze_hours" :min="0" :max="24" :step="1" :precision="0"/>
            </div>
            <div class="col-md-4 col-6">
              <label class="text-white">Optimize for</label>
              <el-select style="width: 100%" class="select-primary" v-model="group.reoptimize_resource_assignments_objective" placeholder="Pack schedules">
                <el-option value="pack" label="Pack schedules" />
                <el-option value="open_slots" label="Open the most slots" />
                <el-option value="spread" label="Spread work evenly" />
              </el-select>
            </div>
          </div>
          <div class="row mt-2" v-if="group.reoptimize_resource_assignments">
            <div class="col">
              <info-hint variant="neutral" icon="icon-bulb-63">
                Pack schedules keeps each resource's bookings close together. Spread work evenly balances assigned time across resources. Both only choose between resources with the same priority, so resource priority always wins. Open the most slots picks the arrangement that leaves the most of that day's open slots bookable, and may place a booking on a lower priority resource to do it.
              </info-hint>
            </div>
          </div>
          <!-- Block slots without resource coverage: hidden until the backend logic ships
          <div class="row">
            <div class="col">
              <el-switch active-color="#e14eca"
                         v-model="group.block_uncovered_slots"  />
              <label> &nbsp; Block slots without resource coverage
                <el-popover trigger="hover" placement="right" width="260" popper-class="otc-pop">
                  <div>
                    <div class="popover-body">When on, slots that no resource covers are blocked automatically when nearby bookings are evaluated.
                    </div>
                  </div>
                  <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                </el-popover>
              </label>
            </div>
          </div>
          -->
          </collapsible-section>

          <collapsible-section title="Preferences" icon="tim-icons icon-bulb-63" class="mt-4">
            <div class="row">
              <div class="col-lg-4">
                <base-input label="Default landing page">
                  <el-select
                    v-model="group.default_landing_page"
                    class="select-primary"
                    name="default_landing_page"
                    placeholder="Bookings (default)"
                    clearable
                  >
                    <el-option
                      v-for="opt in landingPageOptions"
                      class="select-primary"
                      :label="opt.label"
                      :value="opt.key"
                      :key="'LP' + opt.key"
                    ></el-option>
                  </el-select>
                </base-input>
              </div>
            </div>
          </collapsible-section>

          <div v-if="!isCreateMode" class="row mt-4">
            <div class="col">
              <base-button v-on:click="saveResourceSettings()" type="primary">
                Update system settings
              </base-button>
            </div>
          </div>
        </div>

      </card>
    </div>
  </div>

<!--  UTM Link builder-->
  <modal :show.sync="modals.link_builder"
         body-classes="p-0"
         modal-classes="modal-dialog-centered modal-lg">
    <card type="secondary"
          header-classes="bg-white pb-5"
          body-classes="px-lg-5 py-lg-5"
          class="border-0 mb-0">

      <h4 class="card-title">Link builder</h4>
      <base-alert type="info" class="mb-4">
        <i class="tim-icons icon-alert-circle-exc"></i>
        Use this tool to create a trackable booking link for ads, emails, social media, or referral partners. Copy and paste this link wherever you're promoting your bookings or purchases.
      </base-alert>

      <div class="row">
        <div class="col">
          <el-switch active-color="#e14eca"
                     v-model="linkBuilder.is_embedded"  v-on:input="updateBaseUrl()"/>
          <label> &nbsp; My booking page is embedded</label>
        </div>
      </div>
      <div class="row" v-if="linkBuilder.is_embedded">
        <div class="col">
          <base-input v-model="linkBuilder.baseUrl" label="Parent domain name" placeholder="https://yourdomain.com/booking" ></base-input>
        </div>
      </div>

      <div class="row">
        <div class="col-lg-4">
          <label class="form-label">Source
            <el-popover trigger="hover" placement="right">
              <div>
                <div class="popover-body">Represents the specific origin or platform where website traffic is coming from, such as Google, Facebook, or Morty.
                </div>
              </div>
              <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
            </el-popover>
          </label>
          <base-input v-model="linkBuilder.source" placeholder="E.g., google, facebook" ></base-input>
        </div>
        <div class="col-lg-4">
          <label class="form-label">Medium
            <el-popover trigger="hover" placement="right">
              <div>
                <div class="popover-body">Identifies the marketing channel or medium used to deliver a link to a website, for example email, social, cpc.
                </div>
              </div>
              <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
            </el-popover>
          </label>
          <base-input v-model="linkBuilder.medium" placeholder="E.g., cpc, email, social" ></base-input>
        </div>
        <div class="col-lg-4">
          <label class="form-label">Campaign
            <el-popover trigger="hover" placement="right">
              <div>
                <div class="popover-body">Identifies a specific marketing campaign or promotion when tracking website traffic.
                </div>
              </div>
              <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
            </el-popover>
          </label>
          <base-input  v-model="linkBuilder.campaign" placeholder="E.g., summer_sale" ></base-input>
        </div>
      </div>

      <hr style="border-top: 1px solid #f4f5f7;">

      <div class="row mt-4">
        <div class="col-12">
          <label class="form-label">Generated Tracking Link</label>
          <base-input type="primary" class="text-monospace">
            <el-input
              :value="generatedLink"
              readonly
              class="w-100"
              size="medium"
              placeholder="Generated link will appear here"
            >
              <template #append>
                <button class="btn-link btn-icon"
                        type="primary"
                        size="medium"
                        @click="copyLink"
                >
                  <i :class="copied ? 'tim-icons el-icon-check' : 'tim-icons icon-single-copy-04'" class="text-white"></i>
                </button>
              </template>
            </el-input>
          </base-input>
        </div>
      </div>
    </card>
  </modal>
</div>
</template>

<script>
import {Auth} from "aws-amplify";
import {API_LOCATION} from "@/api-config";
import axios from "axios";
import swal from "sweetalert2";
import currencies from "@/assets/currencies.json";
import {diacriticIncludes} from "@/util/search";
import date_formats from "@/assets/date_formats.json";
import time_formats from "@/assets/time_formats.json";
import days_of_the_week from "@/assets/days_of_the_week.json";
import countries from "@/assets/countries.json";
import number_formats from "@/assets/number_formats.json";
import { BTable } from 'bootstrap-vue'
import _ from "lodash";
import states from "@/assets/states.json";
import Breadcrumb from "@/components/Breadcrumb/Breadcrumb";
import BreadcrumbItem from "@/components/Breadcrumb/BreadcrumbItem";
import Badge from "@/components/Badge"
import {BaseAlert} from 'src/components';
import moment from 'moment-timezone'
import {Autocomplete, Switch, TimePicker} from "element-ui";
import { pickerTimeFormat as resolvePickerTimeFormat, parseTime } from '@/plugins/dates';
import PhoneNumberInput from '@/components/PhoneNumberInput.vue';
import TabRail from '@/components/TabRail.vue';
import CollapsibleSection from '@/pages/Bookings/BookinSiteSettings/components/shared/CollapsibleSection.vue';
import VenuePinField from './VenuePinField.vue';
import AddressSearchInput from '@/components/AddressSearchInput.vue';
import { groupFieldsFromAddress } from '@/util/addressSearch';

export default {
  name: "GroupSettings",
  components: {
    BreadcrumbItem,
    Breadcrumb,
    BTable,
    Badge,
    [Switch.name]: Switch,
    BaseAlert,
    [Autocomplete.name]: Autocomplete,
    [TimePicker.name]: TimePicker,
    PhoneNumberInput,
    TabRail,
    CollapsibleSection,
    VenuePinField,
    AddressSearchInput
  },
  computed: {
    companyIdForPin() {
      return localStorage.getItem('companyId')
    },
    groupHasAddress() {
      const g = this.group || {}
      const filled = v => v !== null && v !== undefined && String(v).trim() !== ''
      return filled(g.address_line_1) && (filled(g.city) || filled(g.zip))
    },
    dayRolloverPickerFormat() {
      const fmt = this.group && this.group.time_format && this.group.time_format.format
      return resolvePickerTimeFormat(fmt)
    },
    filteredTimezones() {
      if (!this.tzQuery) return this.timezones
      return this.timezones.filter(this.createFilter(this.tzQuery))
    },
    countryZoneValues() {
      const code = this.group && this.group.country ? this.group.country.code : ''
      if (!code) return []
      return moment.tz.zonesForCountry(code) || []
    },
    countryTimezones() {
      if (!this.countryZoneValues.length) return []
      const zones = new Set(this.countryZoneValues)
      return this.filteredTimezones.filter(tz => zones.has(tz.value))
    },
    otherTimezones() {
      if (!this.countryZoneValues.length) return this.filteredTimezones
      const zones = new Set(this.countryZoneValues)
      return this.filteredTimezones.filter(tz => !zones.has(tz.value))
    },
    visibleOtherTimezones() {
      const slice = this.otherTimezones.slice(0, this.tzLimit)
      // The selected zone must render at least once or el-select falls back to showing
      // the raw value instead of the formatted label. Only forced while unsearched:
      // el-select keeps a selected option in cachedOptions after it unmounts, so once
      // the unfiltered list has rendered it, searching can safely filter it out.
      if (this.tzQuery) return slice
      const selected = this.group && this.group.tz_name
        ? _.find(this.timezones, { value: this.group.tz_name }) : null
      const shownInCountryGroup = selected
        && this.countryTimezones.some(tz => tz.value === selected.value)
      if (selected && !shownInCountryGroup && !slice.some(tz => tz.value === selected.value)) {
        slice.push(selected)
      }
      return slice
    },
    hasMoreTimezones() {
      return this.otherTimezones.length > this.tzLimit
    },
    countryTimezoneLabel() {
      return this.group && this.group.country ? this.group.country.name : ''
    },
    otherTimezoneLabel() {
      return this.countryTimezones.length ? 'Other timezones' : 'All timezones'
    },
    categories() {
      const tabs = [
        { name: 'Location details', shortName: 'Location' },
        { name: 'Locale and formatting', shortName: 'Formats' },
        { name: 'System settings', shortName: 'System' }
      ];
      // Only show Integrations tab when editing existing group
      if (!this.isCreateMode) {
        tabs.push({ name: 'Integrations', shortName: 'Integrations' });
      }
      return tabs;
    },
    embedded_text(){
      return `window.addEventListener('message',function(e){if(e.origin!=='https://offthecouch.io'||!e.data)return;if(e.data.pixelHandshake==='hello'){if(window.fbq&&e.source&&e.source.postMessage){e.source.postMessage({pixelHandshake:'ready'},e.origin);}return;}if(e.data.pixelEvent){if(window.fbq){var p={value:Number(e.data.eventValue)||0,currency:e.data.currency};if(e.data.orderId){fbq('track',e.data.pixelEvent,p,{eventID:String(e.data.orderId)});}else{fbq('track',e.data.pixelEvent,p);}}return;}if(!e.data.eventType||!window.gtag)return;if(e.data.eventType==='page_view'){gtag('event','page_view');return;}gtag('event',e.data.eventType,{currency:e.data.currency,value:Number(e.data.eventValue)||0});});\n`
    },
    generatedLink() {
      try {
        const url = new URL(this.linkBuilder.baseUrl || 'https://yourdomain.com/booking');
        if (this.linkBuilder.source) url.searchParams.set('utm_source', this.linkBuilder.source);
        if (this.linkBuilder.medium) url.searchParams.set('utm_medium', this.linkBuilder.medium);
        if (this.linkBuilder.campaign) url.searchParams.set('utm_campaign', this.linkBuilder.campaign);
        if (this.linkBuilder.content) url.searchParams.set('utm_content', this.linkBuilder.content);
        return url.toString();
      } catch {
        return '';
      }
    }
  },
  data(){
    return {
      landingPageOptions: [
        { key: 'bookings',   label: 'Bookings' },
        { key: 'dashboard',  label: 'Dashboard' },
        { key: 'games',      label: 'Game tracker' },
        { key: 'mail',       label: 'Mail' },
        { key: 'purchases',  label: 'Purchases' },
        { key: 'projects',   label: 'Project management' },
        { key: 'customers',  label: 'Customers' },
        { key: 'waivers',    label: 'Waivers' },
        { key: 'settings',   label: 'Settings' },
      ],
      countryCode: JSON.parse(localStorage.getItem('company')).country,
      currency_symbol: '$',
      show_dynamic_variables: false,
      edit_gcal: '',
      gcal_private_key: null,
      isCreateMode: false,
      loading: false,
      linkBuilder: {
        baseUrl: 'https://offthecouch.io/book/',
        source: '',
        medium: '',
        campaign: '',
        content: '',
        is_embedded: false
      },
      copied: false,
      group: {
        country:{
          "city_label": "City",
          "state_label": "State",
          "zip_label": "Zip",
          "zip_placeholder": "95133",
          "phone_placeholder": "(212) 555-1234"
        },
        require_full_buffer_coverage: false,
        cross_game_buffers: true,
        block_uncovered_slots: false,
        reoptimize_resource_assignments: false,
        reoptimize_freeze_hours: 2,
        reoptimize_resource_assignments_objective: 'pack',
        default_locale: 'en'
      },
      games: '',
      file: '',
      uploadUrl: '',
      activeTabIndex: 0,
      timezones: [],
      tzQuery: '',
      tzLimit: 50,
      currencies: currencies.currencies,
      date_formats: date_formats.dateFormats,
      currency_formats: currencies.formats,
      time_formats: time_formats.timeFormats,
      days_of_the_week: days_of_the_week.daysOfTheWeek,
      number_formats: number_formats.numberFormats,
      countries: countries.countries,
      states: states.states,
      supported_languages: [
        { code: 'en', label: 'English' },
        { code: 'fr', label: 'Français (French)' },
        { code: 'es', label: 'Español (Spanish)' },
        { code: 'sv', label: 'Svenska (Swedish)' },
        { code: 'el', label: 'Ελληνικά (Greek)' },
        { code: 'da', label: 'Dansk (Danish)' }
      ],
      fields: ['game', 'actions'],
      errs: {
        group_name: '',
        group_code: '',
        gcal_client_email: '',
        gcal_private_key: '',
        calendar_id: '',
        gcal_event_title: '',
        default_locale: ''
      },
      integrations: [],
      productivity_integrations: [],
      selectedIntegration: '',
      modals: {
        link_builder: false
      }
    }
  },
  methods:{
    downloadGTMTemplate() {
      const link = document.createElement('a');
      link.href = '/gtm-import-template.json';
      link.download = 'otc-gtm-import-template.json';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    handlePhoneUpdate(phoneData) {
      this.group.phoneE164 = phoneData.e164;
    },
    updateBaseUrl(){
      if(this.linkBuilder.is_embedded){
        this.linkBuilder.baseUrl = ''
      } else {
        this.linkBuilder.baseUrl = 'https://offthecouch.io/book/'
      }
    },
    copyLink() {
      if (!this.generatedLink) return;
      navigator.clipboard.writeText(this.generatedLink).then(() => {
        this.copied = true;
        setTimeout(() => {
          this.copied = false;
        }, 3000);
      });
    },
    openLinkBuilder(){
      this.modals.link_builder = true
    },
    handleTimezoneChange(value){
      const item = _.find(this.timezones, { value: value })
      if(!item) return
      this.group.timezone = item.name
      this.group.timezone_object = item
    },
    filterTimezones(query){
      this.tzQuery = query || ''
      this.tzLimit = 50
    },
    loadMoreTimezones(){
      this.tzLimit += 50
    },
    onTimezoneDropdownToggle(){
      this.tzQuery = ''
      this.tzLimit = 50
    },
    createFilter(queryString) {
      return (timezone) => {
        // Normalize query for case-insensitive matching
        const lowerCaseQuery = queryString.toLowerCase();

        // Check if the query matches any field of the timezone object
        return (
          diacriticIncludes(timezone.value, lowerCaseQuery) || // Match on timezone name
          diacriticIncludes(timezone.abbr, lowerCaseQuery) || // Match on abbreviation
          timezone.offset.includes(lowerCaseQuery) // Match on offset
        );
      };
    },
    copy(item) {
      navigator.clipboard.writeText(item);
    },
    cancelIntegration(){
      if(this.edit_gcal && this.selectedIntegration.id === 4){
        this.edit_gcal = false
      } else {
        this.selectedIntegration = ''
      }
    },
    configureIntegration(item){
      this.selectedIntegration = item
    },
    switchTab(index){
      this.activeTabIndex = index
    },
    handleImageUpload(){
      let file = event.target.files[0];
      this.group.img = URL.createObjectURL(file);
      this.file = file
      this.$emit('change', file);
    },
    getAuthToken() {
      return new Promise(function (resolve) {
        Auth.currentSession()
          .then(data => {
            resolve(data.idToken.jwtToken)
          })
          .catch(err => console.log(err))
      })
    },
    async getSignedURL (file) {
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let url = API_LOCATION + 'upload'

      let payload = {
        filePath: file.name,
        contentType: file.type
      }

      return new Promise ( function(resolve) {
        axios.post(url, payload, config)
          .then((res) => {
            resolve(res.data)
          })
          .catch((err) => {
            console.error(err)
          })
      })
    },
    async saveFile() {
      if(this.file != ''){
        this.uploadUrl = await this.getSignedURL(this.file)
        let uploaded = await this.uploadFile(this.file)
        //Get the image URL
        let imgUrl = this.uploadUrl.split("?")
        this.group.img = imgUrl[0]
      }
    },
    uploadFile (file) {
      let url = this.uploadUrl
      let config = { headers: {
          'Content-Type': file.type
        }}

      let payload = file
      return new Promise ( function(resolve) {
        axios.put(url, payload, config)
          .then((res) => {
            resolve(res)
          })
          .catch((err) => {
            console.error(err)
          })
      })
    },
    validFields(){
      let errors = 0

      // Validate group name
      if(!this.group.name || this.group.name.trim().length <= 0){
        this.errs.group_name = 'Group name cannot be empty'
        errors++
      } else {
        this.errs.group_name = ''
      }

      // Validate group code
      if(this.hasWhiteSpace(this.group.code)){
        this.errs.group_code = 'Group code cannot contain spaces'
        errors++
      } else if(this.group.code.length > 25){
        this.errs.group_code = 'Group code cannot be more than 25 characters'
        errors++
      } else if(this.group.code.length <= 0){
        this.errs.group_code = 'Group code cannot be empty'
        errors++
      } else {
        this.errs.group_code = ''
      }

      // Validate default language
      if(!this.supported_languages.some(l => l.code === this.group.default_locale)){
        this.errs.default_locale = 'Select a default language'
        errors++
      } else {
        this.errs.default_locale = ''
      }
      return errors > 0 ? false : true
    },
    hasWhiteSpace(s) {
      return /\s/g.test(s);
    },
    validateGcalFields(){
      let errors = 0

      if(this.edit_gcal && (this.gcal_private_key === null || this.gcal_private_key.length <= 0)){
        this.errs.gcal_private_key = 'Private key cannot be empty'
        errors++
      } else {
        this.errs.gcal_private_key = ''
      }

      if(this.edit_gcal && this.group.calendar_id.length <= 0){
        this.errs.calendar_id = 'Calendar ID cannot be empty'
        errors++
      } else {
        this.errs.calendar_id = ''
      }

      if(this.edit_gcal && this.group.gcal_client_email.length <= 0){
        this.errs.gcal_client_email = 'Service account email cannot be empty'
        errors++
      } else {
        this.errs.gcal_client_email = ''
      }

      if(this.group.gcal_event_title.length <= 0){
        this.errs.gcal_event_title = 'Calendar event summary cannot be empty'
        errors++
      } else {
        this.errs.gcal_event_title = ''
      }

      return errors > 0 ? false : true
    },
    async updateIntegration(){
      if(this.selectedIntegration.id === 4 && !this.validateGcalFields()){
        return
      }

      this.loading = true
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let groupId = this.$route.query.id

      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + groupId + '/integrations'
      let data = {
        pixel_id: this.group.pixel_id,
        pixel_active: this.group.pixel_active,
        google_tag_container_id: this.group.google_tag_container_id,
        google_tag_active: this.group.google_tag_active,
        ga4_active: this.group.ga4_active,
        ga4_id: this.group.ga4_id,
        send_full_cart_amount_to_ga: this.group.send_full_cart_amount_to_ga,
        send_full_cart_amount_to_pixel: this.group.send_full_cart_amount_to_pixel
      }
      if(this.selectedIntegration.id === 4){
        data = {
          gcal_active: this.group.gcal_active,
          gcal_event_title: this.group.gcal_event_title,
          gcal_event_description: this.group.gcal_event_description
        }
        if(this.gcal_private_key){
          data.gcal_private_key = this.gcal_private_key
          data.calendar_id = this.group.calendar_id
          data.gcal_client_email = this.group.gcal_client_email
        }
      }
      axios.put(url, data, config)
        .then(response => {
          swal('Success!', 'Integration has been updated.', 'success')
          this.selectedIntegration = ''
          this.getGroup()
        })
        .catch(err => {
          if(err.response && err.response.status === 401){
            swal({
              title: 'Error',
              html: 'You do not have access to this feature. Reach out to your admin regarding your site permissions.',
              type: 'error',
            }).then((response)=> {
              this.$router.push('/')
            })
          } else if(err.response && err.response.status === 500){
            swal('Error!', 'A server error occurred', 'error')
          } else {
            swal('Error!', 'Something went wrong', 'error')
          }
        })
        .finally(response => {
          this.loading = false
          this.gcal_private_key = null
        })
    },
    async saveResourceSettings(){
      this.loading = true
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let groupId = this.$route.query.id

      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + groupId
      let data = {
        require_full_buffer_coverage: this.group.require_full_buffer_coverage,
        cross_game_buffers: this.group.cross_game_buffers,
        block_uncovered_slots: this.group.block_uncovered_slots,
        reoptimize_resource_assignments: this.group.reoptimize_resource_assignments,
        reoptimize_freeze_hours: this.group.reoptimize_freeze_hours,
        reoptimize_resource_assignments_objective: this.group.reoptimize_resource_assignments_objective,
        default_landing_page: this.group.default_landing_page || null,
        resource_settings: true
      }

      try {
        await axios.put(url, data, config)
        swal('Success!', 'System settings have been updated.', 'success')
      } catch (err) {
        if(err.response && err.response.status === 401){
          swal({
            title: 'Error',
            html: 'You do not have access to this feature. Reach out to your admin regarding your site permissions.',
            type: 'error',
          }).then((response)=> {
            this.$router.push('/')
          })
        } else if(err.response && err.response.status === 500){
          swal('Error!', 'A server error occurred', 'error')
        } else {
          swal('Error!', 'Something went wrong', 'error')
        }
      } finally {
        this.loading = false
      }
    },
    async savePurchaseTracking(){
      this.loading = true
      await this.saveFile()
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let groupId = this.$route.query.id

      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + groupId
      let data = {
        enable_utm_source_tracking: this.group.enable_utm_source_tracking,
        integration_settings: true
      }

      axios.put(url, data, config)
        .then(response => {
          swal('Success!', 'Purchase tracking settings have been updated.', 'success')
          this.getGroup()
        })
        .catch(err => {
          if(err.response && err.response.status === 401){
            swal({
              title: 'Error',
              html: 'You do not have access to this feature. Reach out to your admin regarding your site permissions.',
              type: 'error',
            }).then((response)=> {
              this.$router.push('/')
            })
          } else if(err.response && err.response.status === 500){
            swal('Error!', 'A server error occurred', 'error')
          } else {
            swal('Error!', 'Something went wrong', 'error')
          }
        })
        .finally(() => {
          this.loading = false
        })
    },
    // A suggested address was picked (AddressSearchInput, OTC-2926): fill the
    // address fields from its geocode and move the venue pin preview there.
    // Address line 2 is left as it is.
    onAddressPick(address) {
      const f = groupFieldsFromAddress(address, this.countries, this.states)
      this.group.address_line_1 = f.address_line_1
      this.group.city = f.city
      this.group.state = f.state
      this.group.zip = f.zip
      if (f.country) {
        const c = f.country
        this.group.country = {name: c.name, code: c.code, city_label: c.city_label,
          state_label: c.state_label, zip_label: c.zip_label, zip_placeholder: c.zip_placeholder,
          phone_placeholder: c.phone_placeholder}
      }
      if (this.$refs.venuePin) this.$refs.venuePin.previewAddressPin(address.latitude, address.longitude)
    },
    async updateGroup(){
      if(!this.validFields()){
        return
      }
      this.loading = true
      await this.saveFile()
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let groupId = this.$route.query.id

      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + groupId
      let data = {...this.group}
      data.timezone = {
        value: data.timezone_object.value,
        abbr: data.timezone_object.abbr,
        offset: moment.tz(data.timezone_object.value).utcOffset() / 60
      }
      data.phone  = this.group.phoneE164 || this.group.phone
      data.day_rollover_time = this.group.day_rollover_time
        ? moment(this.group.day_rollover_time).format('HH:mm:ss')
        : '00:00:00'
      try {
        await axios.put(url, data, config)
      } catch (err) {
        if(err.response && err.response.status === 401){
          this.$router.push('/forbidden')
        } else {
          // 4xx bodies are validation messages worth showing; a 5xx body is server text.
          const status = err.response ? err.response.status : 0
          const message = status >= 400 && status < 500 && typeof err.response.data === 'string' && err.response.data
            ? err.response.data
            : "The location details couldn't be saved. Try again."
          this.$notify({ type: 'danger', message: message, icon: 'tim-icons icon-alert-circle-exc' })
        }
        this.loading = false
        return
      }
      // The venue pin saves after the address, so a manual pin records the new
      // address and shows no "address changed" notice (OTC-2926).
      let pinSaved = true
      const pin = this.$refs.venuePin
      if (pin && pin.hasPendingChange()) {
        try {
          await pin.savePending()
        } catch (e) {
          pinSaved = false
        }
      }
      if (pinSaved) {
        this.$notify({ type: 'success', message: 'Group has been updated.', icon: 'tim-icons icon-check-2' })
      } else {
        this.$notify({ type: 'warning', message: "Location details saved, but the venue pin couldn't be saved. Try again.", icon: 'tim-icons icon-alert-circle-exc' })
      }
      this.getGroup()
      if (pin) await pin.reload()
      this.loading = false
    },
    // changeCurrencyFormats(){
    //   this.currency_symbol = _.find(this.currencies, { code: this.group.currency }).symbol
    //   this.currency_formats[0].example = this.currency_symbol + '12.34'
    //   this.currency_formats[1].example = '12.34 ' + this.group.currency
    //
    //   if(this.group.currency === 'SEK'){
    //     this.currency_formats[0].example = '12.34 kr'
    //   }
    // },
    changeCurrencyFormats() {
      const currencyInfo = _.find(this.currencies, { code: this.group.currency });
      this.currency_symbol = currencyInfo.symbol;
      const isPostfix = currencyInfo.postfix;

      // Update currency format examples based on symbol position
      if (isPostfix) {
        this.currency_formats[0].example = '12.34' + this.currency_symbol;  // 12.34kr
      } else {
        this.currency_formats[0].example = this.currency_symbol + '12.34';  // $12.34
      }

      this.currency_formats[1].example = '12.34 ' + this.group.currency;
    },
    initializeNewGroup() {
      const defaultTimezone = _.find(this.timezones, { value: 'America/Los_Angeles' });
      this.group = {
        name: '',
        code: '',
        company_number: '',
        description: '',
        img: null,
        email: '',
        phone: '',
        address_line_1: '',
        address_line_2: '',
        city: '',
        state: '',
        zip: '',
        country: {
          name: 'United States',
          code: 'US',
          city_label: 'City',
          state_label: 'State',
          zip_label: 'Zip',
          zip_placeholder: '12345',
          phone_placeholder: '(212) 555-1234'
        },
        currency: 'USD',
        timezone: defaultTimezone ? defaultTimezone.name : 'America/Los_Angeles',
        tz_name: 'America/Los_Angeles',
        timezone_object: defaultTimezone,
        date_format: _.find(this.date_formats, { format: 'MM/DD/YYYY' }),
        time_format: _.find(this.time_formats, { format: 'hh:mm' }),
        first_day_of_week: _.find(this.days_of_the_week, { id: 1 }),
        currency_format: _.find(this.currency_formats, { id: 0 }),
        number_format: _.find(this.number_formats, { id: 0 }),
        show_day_of_week: true,
        day_rollover_time: parseTime('00:00:00'),
        default_locale: 'en',
        require_full_buffer_coverage: false,
        cross_game_buffers: true,
        block_uncovered_slots: false,
        reoptimize_resource_assignments: false,
        reoptimize_freeze_hours: 2,
        reoptimize_resource_assignments_objective: 'pack'
      };
      this.changeCurrencyFormats();
    },
    async createGroup() {
      // Validate required fields
      if (!this.validFields()) {
        return;
      }

      this.loading = true;

      // Upload image if one was selected
      await this.saveFile();

      let token = await this.getAuthToken();
      let config = { headers: { Authorization: token } };
      let companyId = localStorage.getItem('companyId');
      let url = API_LOCATION + 'companies/' + companyId + '/groups';

      // Prepare data in the format expected by the backend
      let data = {
        name: this.group.name,
        code: this.group.code,
        company_number: this.group.company_number || '',
        description: this.group.description || '',
        img: this.group.img,
        email: this.group.email || '',
        phone: this.group.phoneE164 || this.group.phone || '',
        address_line_1: this.group.address_line_1 || '',
        address_line_2: this.group.address_line_2 || '',
        city: this.group.city || '',
        state: this.group.state || '',
        zip: this.group.zip || '',
        country: this.group.country ? this.group.country.code : 'US',
        currency: this.group.currency || 'USD',
        timezone: this.group.tz_name || 'America/Los_Angeles',
        date_format: this.group.date_format ? this.group.date_format.format : 'MM/DD/YYYY',
        time_format: this.group.time_format ? this.group.time_format.format : 'hh:mm',
        first_day_of_week: this.group.first_day_of_week != null ? this.group.first_day_of_week.id : 1,
        currency_format: this.group.currency_format != null ? this.group.currency_format.id : 0,
        number_format: this.group.number_format != null ? this.group.number_format.id : 0,
        show_day_of_week: this.group.show_day_of_week ? 1 : 0,
        day_rollover_time: this.group.day_rollover_time
          ? moment(this.group.day_rollover_time).format('HH:mm:ss')
          : '00:00:00',
        default_locale: this.group.default_locale || 'en',
        default_landing_page: this.group.default_landing_page || null,
        require_full_buffer_coverage: this.group.require_full_buffer_coverage ? 1 : 0,
        cross_game_buffers: this.group.cross_game_buffers ? 1 : 0,
        block_uncovered_slots: this.group.block_uncovered_slots ? 1 : 0,
        reoptimize_resource_assignments: this.group.reoptimize_resource_assignments ? 1 : 0,
        reoptimize_freeze_hours: this.group.reoptimize_freeze_hours,
        reoptimize_resource_assignments_objective: this.group.reoptimize_resource_assignments_objective
      };

      axios.post(url, data, config)
        .then(response => {
          swal('Success!', 'Location has been created.', 'success');
          // Navigate back to company settings
          this.$router.push('/settings/company');
        })
        .catch(err => {
          console.log(err);
          if (err.response && err.response.status === 401) {
            swal({
              title: 'Error',
              html: 'You do not have access to this feature. Reach out to your admin regarding your site permissions.',
              type: 'error',
            }).then(() => {
              this.$router.push('/');
            });
          } else {
            swal('Error!', err.response && err.response.data ? err.response.data : 'Something went wrong while creating the location.', 'error');
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    async getGroup(){
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let groupId = this.$route.query.id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + groupId

      this.loading = true
      axios.get(url, config)
        .then(response => {
          localStorage.removeItem('editing')
          this.group = response.data.company_group
          this.games = response.data.games
          //Set correct locale and formats
          this.group.date_format = _.find(this.date_formats, { format: this.group.date_format });
          this.group.time_format = _.find(this.time_formats, { format: this.group.time_format });
          this.group.first_day_of_week = _.find(this.days_of_the_week, { id: this.group.first_day_of_week });
          this.group.number_format = _.find(this.number_formats, { id: this.group.number_format });
          this.group.day_rollover_time = parseTime(this.group.day_rollover_time || '00:00:00')
          if(this.group.currency === 'kr'){
            this.group.currency_format = 0
            this.group.currency = 'SEK'
          }
          this.changeCurrencyFormats()

          this.group.currency_format = _.find(this.currency_formats, { id: this.group.currency_format });

          this.group.country = _.find(this.countries, { code: this.group.country })
          if(!this.group.country){
            this.group.country = {code: ''}
          }

          this.group.timezone_object = _.find(this.timezones, {value: this.group.tz_name});
          if(!this.group.timezone_object && this.group.tz_name){
            // Saved zone is a deprecated alias no longer offered in the list;
            // keep it resolvable so the group loads and saves until a real zone is picked
            const zone = moment.tz.zone(this.group.tz_name)
            this.group.timezone_object = {
              value: this.group.tz_name,
              abbr: zone ? moment.tz(this.group.tz_name).format('z') : '',
              offset: zone ? moment.tz(this.group.tz_name).format('Z') : '',
              name: this.group.tz_name + (zone ? ' - ' + moment.tz(this.group.tz_name).format('z')
                + ' (' + moment.tz(this.group.tz_name).format('Z') + ')' : '') + ' (deprecated)'
            }
            this.timezones.unshift(this.group.timezone_object)
          }
          this.group.timezone = this.group.timezone_object.value + ' - '
            + this.group.timezone_object.abbr + ' (' + this.group.timezone_object.offset + ')'
          this.group.show_day_of_week = Boolean(this.group.show_day_of_week)
          this.group.google_tag_active = Boolean(this.group.google_tag_active)
          this.group.ga4_active = Boolean(this.group.ga4_active)
          this.group.pixel_active = Boolean(this.group.pixel_active)
          this.group.gcal_active = Boolean(this.group.gcal_active)
          this.group.enable_utm_source_tracking = Boolean(this.group.enable_utm_source_tracking)
          this.group.send_full_cart_amount_to_ga =  Boolean(this.group.send_full_cart_amount_to_ga)
          this.group.send_full_cart_amount_to_pixel = Boolean(this.group.send_full_cart_amount_to_pixel)
          this.group.require_full_buffer_coverage = Boolean(this.group.require_full_buffer_coverage)
          this.group.cross_game_buffers = Boolean(this.group.cross_game_buffers)
          this.group.block_uncovered_slots = Boolean(this.group.block_uncovered_slots)
          this.group.reoptimize_resource_assignments = Boolean(this.group.reoptimize_resource_assignments)
          this.group.reoptimize_freeze_hours = Number.isInteger(Number(this.group.reoptimize_freeze_hours)) ? Number(this.group.reoptimize_freeze_hours) : 2
          this.group.reoptimize_resource_assignments_objective = ['pack', 'open_slots', 'spread'].includes(this.group.reoptimize_resource_assignments_objective) ? this.group.reoptimize_resource_assignments_objective : 'pack'
          this.edit_gcal = this.group.gcal_client_email ? 0 : 1
          this.integrations = response.data.integrations
          this.productivity_integrations = response.data.productivity_integrations
          this.linkBuilder.baseUrl = 'https://offthecouch.io/book/' + this.group.code
        })
        .catch(err => {
          if(err.response && err.response.status === 401){
            swal({
              title: 'Error',
              html: 'You do not have access to this feature. Reach out to your admin regarding your site permissions.',
              type: 'error',
            }).then((response)=> {
              this.$router.push('/')
            })
          } else {
            swal('Error!', 'Something went wrong', 'error')
          }
        })
        .finally(() => {
          this.loading = false
        })
    },
    fetchTimezonesWithAbbreviations() {
      // Country-linked zones only: excludes deprecated DST-less aliases
      // (Greenwich, GMT, Etc/*) that silently break wall-clock logic in summer
      const zoneSet = new Set();
      moment.tz.countries().forEach((code) => {
        moment.tz.zonesForCountry(code).forEach((zone) => zoneSet.add(zone));
      });
      const timezoneAbbreviations = [...zoneSet].sort().map((timezone) => {
        return {
          value: timezone,
          abbr: moment.tz(timezone).format("z"), // Get the current abbreviation
          offset: moment.tz(timezone).format("Z"),
          name: timezone + ' - ' + moment.tz(timezone).format("z") + ' (' + moment.tz(timezone).format("Z") + ')'
        };
      });
      this.timezones = timezoneAbbreviations; // Update the component's data
    }
  },
  async mounted() {
    await this.fetchTimezonesWithAbbreviations();
    let query = this.$route.query
    if(!query.hasOwnProperty('id')){
      this.isCreateMode = true
      this.initializeNewGroup()
    } else {
      this.isCreateMode = false
      await this.getGroup()
      if (query.tab !== undefined) {
        this.switchTab(Number(query.tab));
      }
    }
  },
  watch: {
    '$route.query.tab'(newTab) {
      if (newTab !== undefined) {
        this.switchTab(Number(newTab));
      }
    }
  }
}
</script>

